"use client";

import clsx from "clsx";
import {
  Calendar,
  CalendarCheck,
  Check,
  CheckCircle,
  ChevronRight,
  Clock,
  DollarSign,
  FileText,
  HardHat,
  Home,
  Phone,
  PieChart,
  RefreshCw,
  Repeat,
  Shield,
  Star,
  User,
  Users,
  XCircle
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import BodyScrollDisabler from "components/BodyScrollDisabler";
import Image from "components/Image";
import Modal from "components/Tiles/commonComponents/Modal";
import { FadeIn } from "components/ui/FadeIn";
import useErrorLogger from "hooks/useErrorLogger";
import { ERROR_TYPES, TOAST_TYPE } from "utils/constants";
import toast from "utils/toast";

// Types
interface ModalConfig {
  title: string;
  subtitle: string;
  confirmText?: string;
  cancelText?: string;
}
interface ButtonProps {
  text: string;
  url: string;
  phone?: string;
  className?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  triggerConfirmationModal?: boolean;
  modalConfig?: ModalConfig;
  submissionConfig: {
    loadingText: string;
    successText: string;
    errorText: string;
  };
}

interface ListItem {
  text: string;
  isPositive?: boolean;
}

interface ComparableItem {
  address: string;
  saleDate: string;
  salePrice: string;
  sqft: string | number;
  pricePerSqft: string | number;
  bedBath: string;
  condition: string;
  distance?: string;
  highlight?: boolean;
  isSubject?: boolean;
  isArv?: boolean;
}

interface InvestmentCalculation {
  title: string;
  icon: string;
  iconBg: string;
  textColor: string;
  calculations: { label: string; value: string; isNegative?: boolean; highlight?: boolean }[];
  summary: {
    profit?: string;
    roi?: string;
    cashFlow?: string;
    cashOnCash?: string;
    capRate?: string;
    timeline?: string;
    cashLeftIn?: string;
  };
}

interface PropertyInformationItem {
  label: string;
  value: string;
}

interface ConditionFeature {
  text: string;
  isPositive: boolean;
}

interface InvestorProfileItem {
  icon: keyof typeof Icons;
  title: string;
}

interface PropertyMatchItem {
  icon: keyof typeof Icons;
  title: string;
  match: boolean;
}

interface ProcessStep {
  number: number;
  title: string;
  description: string;
}

interface StatProps {
  icon: keyof typeof Icons;
  label: string;
  value: string;
  bgColor: string;
}

interface PropertyPageProps {
  hero?: {
    title: string;
    subtitle?: string;
    bullets: ListItem[];
    price: string;
    image?: string;
    badgeText?: string;
    buttons?: ButtonProps[];
  };
  navTabs?: {
    tabs: { title: string; anchor: string; active?: boolean }[];
  };
  investorMatch?: {
    title: string;
    subtitle: string;
    profile: {
      title: string;
      items: InvestorProfileItem[];
    };
    property: {
      title: string;
      items: PropertyMatchItem[];
    };
    matchScore?: {
      score: string;
      description: string;
      image?: string;
      tags: string[];
      cta?: ButtonProps;
    };
  };
  dealHighlight?: {
    price: string;
    stats: {
      title: string;
      description: string;
      icon: string;
      value: string;
      subtext: string;
    }[];
    cta?: ButtonProps;
    expiryText?: string;
  };
  investmentCalculator?: {
    title: string;
    subtitle: string;
    calculations: InvestmentCalculation[];
  };
  propertyDetails?: {
    title: string;
    subtitle?: string;
    information: PropertyInformationItem[];
    condition: {
      summary: string;
      features: ConditionFeature[];
    };
    images?: { src: string; alt: string }[];
    viewAllText?: string;
    buttons?: ButtonProps[];
  };
  propertyTabs?: {
    tabs: { label: string; active?: boolean }[];
  };
  compsAnalysis?: {
    title: string;
    subtitle: string;
    comps: ComparableItem[];
    summary: string[];
  };
  acquisitionCriteria?: {
    title: string;
    subtitle: string;
    description?: string;
    keyPoints: { icon: string; title: string; description: string }[];
    process: { title: string; items: { title: string; description: string }[] };
    highlight?: { title: string; text: string };
  };
  process?: {
    title: string;
    subtitle: string;
    steps: ProcessStep[];
    stats: StatProps[];
  };
  finalCta?: {
    title: string;
    subtitle: string;
    buttons: ButtonProps[];
    expiryText?: string;
  };
  stickyCta?: {
    enabled: boolean;
    title: string;
    subtitle: string;
    button: ButtonProps;
  };
  sectionSort?: {
    hero?: number;
    navTabs?: number;
    investorMatch?: number;
    dealHighlight?: number;
    investmentCalculator?: number;
    propertyDetails?: number;
    compsAnalysis?: number;
    acquisitionCriteria?: number;
    process?: number;
    finalCta?: number;
  };
  submissionUrl?: string;
  dealId?: string;
  redirectSlug?: string;
}

// Create a simple icon lookup object
const Icons = {
  check: Check,
  clock: Clock,
  dollar: DollarSign,
  home: Home,
  phone: Phone,
  star: Star,
  chevronRight: ChevronRight,
  xCircle: XCircle,
  users: Users,
  user: User,
  calendar: Calendar,
  calendarCheck: CalendarCheck,
  pieChart: PieChart,
  refresh: RefreshCw,
  repeat: Repeat,
  shield: Shield,
  hardHat: HardHat,
  fileText: FileText,
  checkCircle: CheckCircle
} as const;

const HeroSection: React.FC<{ data: PropertyPageProps["hero"] }> = ({ data }) => {
  if (!data) return null;

  return (
    <FadeIn>
      <section className="bg-blue-800 py-16 text-white">
        <div className="container mx-auto flex max-w-5xl flex-col items-center px-4 md:flex-row">
          <div className="mb-8 md:mb-0 md:w-1/2">
            <h1 className="md:text-5xl mb-4 text-4xl font-extrabold">{data.title}</h1>
            {data.subtitle && <p className="mb-4 text-xl">{data.subtitle}</p>}
            <ul className="mb-6 space-y-3">
              {data.bullets.map((item, index) => (
                <li key={index} className="flex items-center">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  {item.text}
                </li>
              ))}
            </ul>
            {data.buttons?.map((button, index) =>
              button.triggerConfirmationModal ? (
                <button
                  key={index}
                  className={clsx(
                    "inline-block rounded-lg px-8 py-4 text-center text-lg font-bold transition-all duration-300",
                    button.className ? button.className : "bg-green-600 text-white hover:bg-green-700"
                  )}
                  onClick={button.onClick}
                >
                  {button.text}
                </button>
              ) : (
                <Link
                  key={index}
                  href={button.url}
                  target={button.url.startsWith("#") ? "" : "_blank"}
                  className={clsx(
                    "inline-block rounded-lg px-8 py-4 text-center text-lg font-bold transition-all duration-300",
                    button.className ? button.className : "bg-green-600 text-white hover:bg-green-700"
                  )}
                >
                  {button.text}
                </Link>
              )
            )}
          </div>
          <div className="relative md:w-1/2">
            {data.badgeText && (
              <div className="badge absolute -right-4 -top-4 z-10 rotate-12 transform rounded-full bg-red-500 px-4 py-2 font-bold text-white shadow-lg">
                {data.badgeText}
              </div>
            )}
            {data.image && (
              <Image
                src={data.image}
                alt="Property Exterior"
                width={600}
                height={400}
                className="w-full rounded-lg shadow-lg"
              />
            )}
          </div>
        </div>
      </section>
    </FadeIn>
  );
};

const NavTabsSection: React.FC<{ data: PropertyPageProps["navTabs"] }> = ({ data }) => {
  if (!data) return null;

  return (
    <div className="nav-tabs sticky top-0 z-10 border-b border-gray-200 bg-white py-4 shadow-sm">
      <div className="container mx-auto px-4">
        <ul className="flex flex-wrap justify-center gap-6">
          {data.tabs.map((tab, index) => (
            <li key={index}>
              <a
                href={`#${tab.anchor}`}
                className={clsx(
                  "nav-tab inline-block rounded-full px-6 py-3 font-semibold transition-colors",
                  tab.active ? "bg-blue-800 text-white" : "bg-gray-100 text-blue-800 hover:bg-blue-800 hover:text-white"
                )}
              >
                {tab.title}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const InvestorMatchSection: React.FC<{ data: PropertyPageProps["investorMatch"] }> = ({ data }) => {
  if (!data) return null;

  return (
    <FadeIn>
      <section className="bg-gray-50 py-16" id="match">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="mb-8 text-center">
            <span className="mb-3 inline-block rounded-full bg-blue-100 px-4 py-1 text-sm font-bold text-blue-800">
              PERFECT MATCH
            </span>
            <h2 className="mb-2 text-4xl font-extrabold text-blue-800">{data.title}</h2>
            <p className="mx-auto max-w-3xl text-lg text-gray-700">{data.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {/* Investor Profile */}
            <div className="rounded-lg bg-white p-6 shadow-md transition-all duration-300 hover:shadow-lg">
              <div className="mb-6 flex items-center">
                <div className="mr-4 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-600">
                  <User className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-blue-800">{data.profile.title}</h3>
              </div>

              <div className="space-y-4">
                {data.profile.items.map((item, index) => {
                  const Icon = Icons[item.icon as keyof typeof Icons] || RefreshCw;

                  return (
                    <div key={index} className="flex items-center rounded-lg bg-blue-50 p-4">
                      <Icon className="mr-2 h-5 w-5 text-blue-600" />
                      <div>
                        <h4 className="font-bold text-blue-800">{item.title}</h4>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Property Match */}
            <div className="rounded-lg bg-white p-6 shadow-md transition-all duration-300 hover:shadow-lg">
              <div className="mb-6 flex items-center">
                <div className="mr-4 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-600">
                  <Home className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-green-800">{data.property.title}</h3>
              </div>

              <div className="space-y-4">
                {data.property.items.map((item, index) => {
                  const Icon = Icons[item.icon as keyof typeof Icons] || RefreshCw;

                  return (
                    <div key={index} className="flex items-center justify-between rounded-lg bg-green-50 p-4">
                      <div className="flex items-center">
                        <Icon className="mr-2 h-5 w-5 text-green-600" />
                        <h4 className="font-bold text-green-800">{item.title}</h4>
                      </div>
                      {item.match && (
                        <span className="rounded-full bg-green-600 px-2 py-1 text-xs text-white">MATCH</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Match Score */}
          {data.matchScore && (
            <div className="mt-8 flex flex-col items-center rounded-lg bg-blue-50 p-6 md:flex-row">
              <div className="mb-4 md:mb-0 md:mr-6">
                {data.matchScore.image ? (
                  <Image
                    src={data.matchScore.image}
                    alt="Match Score"
                    width={120}
                    height={120}
                    className="rounded-full border-4 border-green-500"
                  />
                ) : (
                  <div className="flex h-24 w-24 items-center justify-center rounded-full border-4 border-green-500 bg-blue-100">
                    <Star className="h-10 w-10 text-green-500" />
                  </div>
                )}
              </div>
              <div className="text-center md:text-left">
                <h4 className="mb-2 text-2xl font-bold text-blue-800">{data.matchScore.score}</h4>
                <p className="mb-3 text-gray-700">{data.matchScore.description}</p>
                <div className="flex flex-wrap gap-2">
                  {data.matchScore.tags.map((tag, index) => (
                    <span key={index} className="rounded-full bg-green-100 px-3 py-1 text-sm text-green-800">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              {data.matchScore.cta && (
                <div className="ml-auto mt-4 md:mt-0">
                  {data.matchScore.cta.triggerConfirmationModal ? (
                    <button
                      onClick={data.matchScore.cta.onClick}
                      className={clsx(
                        "inline-block rounded-lg px-6 py-3 text-center text-lg font-bold transition-all duration-300",
                        data.matchScore.cta.className
                          ? data.matchScore.cta.className
                          : "bg-green-600 text-white hover:bg-green-700 "
                      )}
                    >
                      {data.matchScore.cta.text}
                    </button>
                  ) : (
                    <Link
                      href={data.matchScore.cta.url}
                      target={data.matchScore.cta.url.startsWith("#") ? "" : "_blank"}
                      className={clsx(
                        "inline-block rounded-lg px-6 py-3 text-center text-lg font-bold transition-all duration-300",
                        data.matchScore.cta.className
                          ? data.matchScore.cta.className
                          : "bg-green-600 text-white hover:bg-green-700 "
                      )}
                    >
                      {data.matchScore.cta.text}
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </FadeIn>
  );
};

const DealHighlightSection: React.FC<{ data: PropertyPageProps["dealHighlight"] }> = ({ data }) => {
  if (!data) return null;

  return (
    <FadeIn>
      <section className="bg-blue-800 py-16" id="numbers">
        <div className="container mx-auto max-w-5xl px-4 text-center">
          <h2 className="mb-8 text-3xl font-extrabold text-white md:text-4xl">
            Wholesale Price: <span className="text-yellow-300">{data.price}</span>
          </h2>

          <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {data.stats.map((stat, index) => {
              const Icon = Icons[stat.icon as keyof typeof Icons] || DollarSign;

              return (
                <div key={index} className="rounded-lg bg-white bg-opacity-10 p-6">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white bg-opacity-20">
                    <Icon className="h-6 w-6 text-yellow-300" />
                  </div>
                  <h3 className="mb-2 text-xl font-bold text-white">{stat.title}</h3>
                  <p className="mb-4 text-green-100">{stat.description}</p>
                  <div className="rounded-lg bg-green-800 p-3">
                    <p className="text-2xl font-bold text-yellow-300">{stat.value}</p>
                    <p className="text-sm text-white">{stat.subtext}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {data.cta &&
            (data.cta?.triggerConfirmationModal ? (
              <button
                onClick={data.cta.onClick}
                className={clsx(
                  "inline-block rounded-lg  px-8 py-4 text-center text-xl font-bold transition-all duration-300",
                  data.cta.className ? data.cta.className : "bg-yellow-500 text-green-900 hover:bg-yellow-600"
                )}
              >
                {data.cta.text}
              </button>
            ) : (
              <Link
                href={data.cta.url}
                target={data.cta.url.startsWith("#") ? "" : "_blank"}
                className={clsx(
                  "inline-block rounded-lg  px-8 py-4 text-xl font-bold transition-all duration-300",
                  data.cta.className
                    ? data.cta.className
                    : "bg-yellow-500 text-center text-green-900 hover:bg-yellow-600"
                )}
              >
                {data.cta.text}
              </Link>
            ))}

          {data.expiryText && (
            <p className="mt-4 text-white">
              <Clock className="mr-1 inline-block h-5 w-5" />
              {data.expiryText}
            </p>
          )}
        </div>
      </section>
    </FadeIn>
  );
};

const InvestmentCalculatorSection: React.FC<{ data: PropertyPageProps["investmentCalculator"] }> = ({ data }) => {
  if (!data) return null;

  return (
    <FadeIn>
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="mb-8 text-center">
            <span className="mb-3 inline-block rounded-full bg-blue-100 px-4 py-1 text-sm font-bold text-blue-800">
              ROI PROJECTION
            </span>
            <h2 className="mb-2 text-4xl font-extrabold text-blue-800">{data.title}</h2>
            <p className="text-lg text-gray-700">{data.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {data.calculations.map((calc, index) => {
              const Icon = Icons[calc.icon as keyof typeof Icons] || RefreshCw;

              return (
                <div
                  key={index}
                  className={`${calc.iconBg} rounded-lg p-6 shadow-md transition-all duration-300 hover:shadow-lg`}
                >
                  <div
                    className={`flex h-14 w-14 items-center justify-center ${calc.iconBg} mx-auto mb-4 rounded-full`}
                  >
                    <Icon className={`h-5 w-5 ${calc.textColor}`} />
                  </div>
                  <h3 className={`text-xl font-bold ${calc.textColor} mb-4 text-center`}>{calc.title}</h3>

                  <div className="space-y-2">
                    {calc.calculations.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex justify-between">
                        <span className="text-gray-700">{item.label}:</span>
                        <span
                          className={clsx(
                            "font-bold",
                            item.isNegative ? "text-red-600" : item.highlight ? "text-green-600" : calc.textColor
                          )}
                        >
                          {item.value}
                        </span>
                      </div>
                    ))}

                    {calc.summary.profit && (
                      <div
                        className={`flex justify-between border-t ${calc.icon === "home" ? "border-green-200" : calc.icon === "repeat" ? "border-purple-200" : "border-blue-200"} mt-2 pt-2`}
                      >
                        <span className="font-bold text-gray-800">Net Profit:</span>
                        <span className="font-bold text-green-600">{calc.summary.profit}</span>
                      </div>
                    )}

                    {calc.summary.roi && (
                      <div className="flex justify-between">
                        <span className="font-bold text-gray-800">ROI:</span>
                        <span className="font-bold text-green-600">{calc.summary.roi}</span>
                      </div>
                    )}

                    {calc.summary.cashFlow && (
                      <div className="flex justify-between">
                        <span className="font-bold text-gray-800">Monthly Cash Flow:</span>
                        <span className="font-bold text-green-600">{calc.summary.cashFlow}</span>
                      </div>
                    )}

                    {calc.summary.cashOnCash && (
                      <div className="flex justify-between">
                        <span className="font-bold text-gray-800">Cash-on-Cash:</span>
                        <span className="font-bold text-green-600">{calc.summary.cashOnCash}</span>
                      </div>
                    )}

                    {calc.summary.capRate && (
                      <div className="flex justify-between">
                        <span className="font-bold text-gray-800">Cap Rate:</span>
                        <span className="font-bold text-green-600">{calc.summary.capRate}</span>
                      </div>
                    )}

                    {calc.summary.cashLeftIn && (
                      <div className="flex justify-between">
                        <span className="font-bold text-gray-800">Cash Left In Deal:</span>
                        <span className="font-bold text-green-600">{calc.summary.cashLeftIn}</span>
                      </div>
                    )}

                    {calc.summary.timeline && (
                      <div className="flex justify-between">
                        <span className="font-bold text-gray-800">Timeline:</span>
                        <span className="font-bold text-blue-800">{calc.summary.timeline}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </FadeIn>
  );
};

const PropertyDetailsSection: React.FC<{
  data: PropertyPageProps["propertyDetails"];
  tabs: PropertyPageProps["propertyTabs"];
}> = ({ data, tabs }) => {
  const [photoId, setPhotoId] = useState<null | number>(null);

  const propertyImages = data?.images?.map((image, index) => ({
    ...image,
    id: index
  }));

  const onChangePhotoId = (newVal: number) => {
    setPhotoId(newVal);
  };

  if (!data) return null;

  return (
    <>
      <FadeIn>
        <section className="py-16" id="details">
          <div className="container mx-auto max-w-5xl px-4">
            <div className="mb-8 text-center">
              <span className="mb-3 inline-block rounded-full bg-blue-100 px-4 py-1 text-sm font-bold text-blue-800">
                PROPERTY OVERVIEW
              </span>
              <h2 className="mb-2 text-4xl font-extrabold text-blue-800">{data.title}</h2>
              {data.subtitle && <p className="text-lg text-gray-700">{data.subtitle}</p>}
            </div>

            {/* Tabs Navigation */}
            {tabs && (
              <div className="mb-8 flex flex-wrap border-b border-gray-200">
                {tabs.tabs.map((tab, index) => (
                  <button
                    key={index}
                    className={clsx(
                      "px-6 py-3 font-semibold",
                      tab.active
                        ? "border-b-2 border-blue-600 bg-blue-50 text-blue-800"
                        : "border-b-2 border-transparent text-gray-600 hover:border-blue-300 hover:text-blue-800"
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Property Information */}
            <div className="mb-8 grid grid-cols-1 gap-8 md:grid-cols-2">
              <div>
                <h3 className="mb-4 text-xl font-bold text-blue-800">Property Information</h3>
                <div className="rounded-lg bg-gray-50 p-5">
                  <div className="grid grid-cols-2 gap-4">
                    {data.information.map((item, index) => (
                      <div key={index}>
                        <p className="text-sm text-gray-500">{item.label}</p>
                        <p className="font-semibold">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Current Condition */}
              <div>
                <h3 className="mb-4 text-xl font-bold text-blue-800">Current Condition & Features</h3>
                <div className="rounded-lg bg-gray-50 p-5">
                  <p className="mb-4">{data.condition.summary}</p>

                  {/* Positives */}
                  <div className="mb-4 space-y-2">
                    {data.condition.features
                      .filter((f) => f.isPositive)
                      .map((feature, index) => (
                        <div key={index} className="flex items-start">
                          <Check className="mr-2 mt-1 h-5 w-5 text-green-500" />
                          <p className="text-gray-700">{feature.text}</p>
                        </div>
                      ))}
                  </div>

                  {/* Negatives */}
                  <div className="space-y-2">
                    {data.condition.features
                      .filter((f) => !f.isPositive)
                      .map((feature, index) => (
                        <div key={index} className="flex items-start">
                          <XCircle className="mr-2 mt-1 h-5 w-5 text-red-500" />
                          <p className="text-gray-700">{feature.text}</p>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Property Images */}
            {propertyImages && propertyImages.length > 0 && (
              <div className="mb-8">
                <h3 className="mb-4 text-xl font-bold text-blue-800">Property Images</h3>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  {propertyImages.slice(0, 4).map((image, index) => (
                    <Image
                      key={index}
                      src={image.src}
                      alt={image.alt}
                      width={300}
                      height={200}
                      className="h-48 w-full cursor-pointer rounded-lg object-cover transition hover:opacity-75"
                      onClick={() => setPhotoId(image.id)}
                    />
                  ))}
                </div>
                {data.viewAllText && propertyImages.length > 4 && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => onChangePhotoId(0)}
                      className="font-semibold text-blue-600 hover:text-blue-800"
                    >
                      {data.viewAllText}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            {data.buttons && data.buttons.length > 0 && (
              <div className="flex justify-center gap-2">
                {data.buttons.map((button, index) =>
                  button.triggerConfirmationModal ? (
                    <button
                      key={index}
                      className={clsx(
                        "rounded-lg px-6 py-3 text-center text-lg font-bold transition-all duration-300",
                        button.className
                          ? button.className
                          : index === 0
                            ? "mr-4 bg-blue-600 text-white hover:bg-blue-700"
                            : "bg-green-600 text-white hover:bg-green-700"
                      )}
                      onClick={button.onClick}
                    >
                      {button.text}
                    </button>
                  ) : (
                    <Link
                      key={index}
                      href={button.url}
                      target={button.url.startsWith("#") ? "" : "_blank"}
                      className={clsx(
                        "rounded-lg px-6 py-3 text-center text-lg font-bold transition-all duration-300",
                        button.className
                          ? button.className
                          : index === 0
                            ? "mr-4 bg-blue-600 text-white hover:bg-blue-700"
                            : "bg-green-600 text-white hover:bg-green-700"
                      )}
                    >
                      {button.text}
                    </Link>
                  )
                )}
              </div>
            )}
          </div>
        </section>
      </FadeIn>
      {photoId !== null && (
        <Modal
          images={propertyImages || []}
          onClose={() => {
            setPhotoId(null);
          }}
          photoId={photoId}
          onChangePhotoId={onChangePhotoId}
        />
      )}
    </>
  );
};

const CompsAnalysisSection: React.FC<{ data: PropertyPageProps["compsAnalysis"] }> = ({ data }) => {
  if (!data) return null;

  return (
    <FadeIn>
      <section className="w-full bg-gray-50 py-16" id="comps">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="mb-8 text-center">
            <span className="mb-3 inline-block rounded-full bg-blue-100 px-4 py-1 text-sm font-bold text-blue-800">
              MARKET ANALYSIS
            </span>
            <h2 className="mb-2 text-4xl font-extrabold text-blue-800">{data.title}</h2>
            <p className="text-lg text-gray-700">{data.subtitle}</p>
          </div>
          
          <div className="mb-8 overflow-x-auto rounded-lg bg-white p-6 shadow-md">
            <table className="w-full border-collapse">
              <thead className="bg-blue-800 text-white">
                <tr>
                  <th className="p-3 text-left">Address</th>
                  <th className="p-3 text-left">Sale Date</th>
                  <th className="p-3 text-right">Sale Price</th>
                  <th className="p-3 text-right">Sq.Ft.</th>
                  <th className="p-3 text-right">Price/Sq.Ft.</th>
                  <th className="p-3 text-center">Bed/Bath</th>
                  <th className="p-3 text-center">Condition</th>
                  <th className="p-3 text-center">Distance</th>
                </tr>
              </thead>
              <tbody>
                {data.comps.map((comp, index) => (
                  <tr
                    key={index}
                    className={clsx(
                      "border-b border-gray-200",
                      comp.isSubject ? "bg-green-50" : comp.isArv ? "bg-blue-100" : "hover:bg-blue-50"
                    )}
                  >
                    <td className="p-3 font-bold">{comp.address}</td>
                    <td className="p-3">{comp.saleDate}</td>
                    <td
                      className={clsx(
                        "p-3 text-right",
                        comp.isSubject ? "font-bold text-green-600" : comp.isArv ? "font-bold" : "font-semibold"
                      )}
                    >
                      {comp.salePrice}
                    </td>
                    <td className="p-3 text-right">{comp.sqft}</td>
                    <td
                      className={clsx(
                        "p-3 text-right",
                        comp.isSubject ? "font-bold text-green-600" : comp.isArv ? "font-bold" : ""
                      )}
                    >
                      {comp.pricePerSqft}
                    </td>
                    <td className="p-3 text-center">{comp.bedBath}</td>
                    <td className="p-3 text-center">{comp.condition}</td>
                    <td className="p-3 text-center">{comp.distance || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-lg bg-blue-50 p-6">
            <div className="flex items-start">
              <PieChart className="mr-3 mt-1 h-6 w-6 text-blue-600" />
              <div>
                <h3 className="mb-2 text-xl font-bold text-blue-800">Comp Analysis Summary</h3>
                <ul className="space-y-2 text-gray-700">
                  {data.summary.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </FadeIn>
  );
};

const AcquisitionCriteriaSection: React.FC<{ data: PropertyPageProps["acquisitionCriteria"] }> = ({ data }) => {
  if (!data) return null;

  return (
    <FadeIn>
      <section className="py-16">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="overflow-hidden rounded-xl bg-white shadow-lg">
            <div className="flex flex-col md:flex-row">
              {/* Left Side */}
              <div className="bg-blue-800 p-8 text-white md:w-1/2">
                <span className="mb-4 inline-block rounded-full bg-yellow-500 px-4 py-1 text-sm font-bold text-blue-900">
                  OUR STANDARDS
                </span>
                <h2 className="mb-6 text-3xl font-extrabold">{data.title}</h2>
                <p className="mb-6 text-lg text-blue-100">{data.subtitle}</p>

                {/* Key Points */}
                <div className="space-y-6">
                  {data.keyPoints.map((point, index) => {
                    const Icon = Icons[point.icon as keyof typeof Icons] || Clock;

                    return (
                      <div key={index} className="flex items-start">
                        <div className="mr-4 flex-shrink-0 rounded-lg bg-blue-700 bg-opacity-40 p-2">
                          <Icon className="h-5 w-5 text-yellow-400" />
                        </div>
                        <div>
                          <h3 className="mb-1 text-xl font-bold">{point.title}</h3>
                          <p className="text-blue-100">{point.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right Side */}
              <div className="bg-white p-8 md:w-1/2">
                <h3 className="mb-6 text-2xl font-bold text-blue-800">Why Our Process Is Different</h3>

                <div className="space-y-4">
                  {data.process.items.map((item, index) => (
                    <div key={index} className="flex items-start">
                      <div className="mr-4 flex-shrink-0 rounded-full bg-green-100 p-2">
                        <Check className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="mb-1 font-bold text-gray-800">{item.title}</h4>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Highlight Box */}
                {data.highlight && (
                  <div className="mt-6 rounded-lg bg-blue-50 p-5">
                    <div className="flex items-center">
                      <svg className="mr-3 h-6 w-6 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                      </svg>
                      <h4 className="font-bold text-blue-800">{data.highlight.title}</h4>
                    </div>
                    <p className="mt-2 text-gray-700">{data.highlight.text}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </FadeIn>
  );
};

const ProcessSection: React.FC<{ data: PropertyPageProps["process"] }> = ({ data }) => {
  if (!data) return null;

  return (
    <FadeIn>
      <section className="bg-gray-50 py-16" id="process">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="mb-10 text-center">
            <span className="mb-3 inline-block rounded-full bg-blue-100 px-4 py-1 text-sm font-bold text-blue-800">
              HOW IT WORKS
            </span>
            <h2 className="mb-2 text-4xl font-extrabold text-blue-800">{data.title}</h2>
            <p className="mx-auto max-w-3xl text-lg text-gray-700">{data.subtitle}</p>
          </div>

          {/* Timeline */}
          <div className="relative mb-12">
            {/* Connection Line */}
            <div className="absolute left-0 right-0 top-24 z-0 hidden h-1 bg-blue-100 md:block"></div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
              {data.steps.map((step, index) => (
                <div key={index} className="relative z-10">
                  <div className="h-full rounded-lg border border-blue-100 bg-white p-6 shadow-md transition-all duration-300 hover:shadow-lg">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 text-xl font-bold text-white md:mx-0">
                      {step.number}
                    </div>
                    <h3 className="mb-3 text-center text-xl font-bold text-blue-800 md:text-left">{step.title}</h3>
                    <p className="text-gray-700">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="flex flex-col items-center justify-center gap-8 md:flex-row">
            {data.stats.map((stat, index) => {
              const Icon = Icons[stat.icon as keyof typeof Icons] || Calendar;

              return (
                <div key={index} className={`${stat.bgColor} w-full rounded-lg p-4`}>
                  <div className="flex items-center">
                    <div
                      className={`h-10 w-10 ${stat.bgColor === "bg-blue-50" ? "bg-blue-100" : stat.bgColor === "bg-green-50" ? "bg-green-100" : "bg-yellow-100"} mr-3 flex items-center justify-center rounded-full`}
                    >
                      <Icon
                        className={`h-5 w-5 ${stat.bgColor === "bg-blue-50" ? "text-blue-600" : stat.bgColor === "bg-green-50" ? "text-green-600" : "text-yellow-600"}`}
                      />
                    </div>
                    <div>
                      <p className="font-bold text-gray-800">{stat.label}</p>
                      <p
                        className={`${stat.bgColor === "bg-blue-50" ? "text-blue-600" : stat.bgColor === "bg-green-50" ? "text-green-600" : "text-yellow-600"} font-semibold`}
                      >
                        {stat.value}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </FadeIn>
  );
};

const FinalCtaSection: React.FC<{ data: PropertyPageProps["finalCta"] }> = ({ data }) => {
  if (!data) return null;

  return (
    <FadeIn>
      <section className="bg-green-600 py-16" id="reserve-deal">
        <div className="container mx-auto max-w-5xl px-4 text-center">
          <h2 className="mb-4 text-3xl font-extrabold text-white md:text-4xl">{data.title}</h2>
          <p className="mx-auto mb-8 max-w-3xl text-xl text-white">{data.subtitle}</p>

          <div className="flex flex-col justify-center gap-4 md:flex-row">
            {data.buttons.map((button, index) => {
              const Icon =
                index === 0
                  ? button.icon ||
                    (() => (
                      <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ))
                  : button.icon ||
                    (() => (
                      <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                    ));

              return button.triggerConfirmationModal ? (
                <button
                  key={index}
                  className={clsx(
                    "inline-flex items-center justify-center rounded-lg px-8 py-4 text-center text-lg font-bold transition-all duration-300",
                    button.className
                      ? button.className
                      : index === 0
                        ? "mr-4 bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-green-600 text-white hover:bg-green-700"
                  )}
                  onClick={button.onClick}
                >
                  {typeof Icon === "function" && typeof Icon !== "object" ? (
                    <Icon />
                  ) : (
                    <span className="mr-2">{button.icon}</span>
                  )}
                  {button.text}
                </button>
              ) : (
                <Link
                  key={index}
                  href={button.url}
                  target={button.url.startsWith("#") ? "" : "_blank"}
                  className={clsx(
                    "inline-flex items-center justify-center rounded-lg px-8 py-4 text-center text-lg font-bold transition-all duration-300",
                    button.className
                      ? button.className
                      : index === 0
                        ? "bg-yellow-500 text-green-900 hover:bg-yellow-600"
                        : "border-2 border-white bg-transparent text-white hover:bg-white hover:text-green-600"
                  )}
                >
                  {typeof Icon === "function" && typeof Icon !== "object" ? (
                    <Icon />
                  ) : (
                    <span className="mr-2">{button.icon}</span>
                  )}
                  {button.text}
                </Link>
              );
            })}
          </div>

          {data.expiryText && (
            <p className="mt-6 text-white">
              <Clock className="mr-1 inline-block h-5 w-5" />
              {data.expiryText}
            </p>
          )}
        </div>
      </section>
    </FadeIn>
  );
};

const StickyCtaSection: React.FC<{ data: PropertyPageProps["stickyCta"] }> = ({ data }) => {
  if (!data || !data.enabled) return null;

  return (
    <div className="show sticky bottom-0 z-10 bg-gray-50 p-2 shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1)]">
      <div className="container mx-auto flex items-center justify-center px-4 md:justify-between">
        <div className="hidden md:block">
          <p className="font-bold text-blue-800">{data.title}</p>
          <p className="text-sm text-gray-600">{data.subtitle}</p>
        </div>

        {data.button.triggerConfirmationModal ? (
          <button
            onClick={data.button.onClick}
            className={clsx(
              "rounded-lg px-5 py-2 text-center font-bold transition-all duration-300",
              data.button.className ? data.button.className : "bg-green-600 text-white hover:bg-green-700"
            )}
          >
            {data.button.text}
          </button>
        ) : (
          <Link
            href={data.button.url}
            target={data.button.url.startsWith("#") ? "" : "_blank"}
            className={clsx(
              "rounded-lg px-5 py-2 text-center font-bold transition-all duration-300",
              data.button.className ? data.button.className : "bg-green-600 text-white hover:bg-green-700"
            )}
          >
            {data.button.text}
          </Link>
        )}
      </div>
    </div>
  );
};

const ConfirmationModal: React.FC<{
  isOpen: boolean;
  isConfirmLoading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  subtitle: string;
  confirmText?: string;
  cancelText?: string;
}> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  subtitle,
  confirmText = "Confirm Offer",
  cancelText = "Cancel",
  isConfirmLoading
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-75">
        <div className="w-full max-w-lg animate-fadeIn rounded-xl border-t-4 border-green-600 bg-white p-8 shadow-lg max-md:mx-3">
          <h3 className="mb-4 text-center font-heading text-3xl font-extrabold text-blue-800">{title}</h3>
          <p className="mb-6 text-center text-lg text-gray-700">{subtitle}</p>
          <div className="flex flex-col gap-4 sm:flex-row">
            <button
              onClick={onClose}
              className="flex-1 rounded-lg bg-gray-200 px-6 py-3 text-lg font-bold text-gray-700 hover:bg-gray-300"
              disabled={isConfirmLoading}
            >
              {cancelText}
            </button>
            <button
              disabled={isConfirmLoading}
              onClick={onConfirm}
              className="flex-1 rounded-lg bg-green-600 px-6 py-3 text-lg font-bold text-white hover:bg-green-700"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
      {isOpen && <BodyScrollDisabler />}
    </>
  );
};

const DEFAULT_CONFIRMAITON_MODAL_CONFIG: ModalConfig = {
  title: "Confirm Your Offer",
  subtitle: "Are you ready to proceed with this offer?",
  confirmText: "Confirm Offer",
  cancelText: "Cancel"
};

const PropertyPage: React.FC<PropertyPageProps> = (props) => {
  const [clickedButtonProps, setClickedButtonProps] = useState<ButtonProps | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [processingConfirmAction, setProcessingConfirmAction] = useState(false);
  const router = useRouter();
  const { logError } = useErrorLogger();

  const handleOpenModal = (btnProps: ButtonProps) => {
    setClickedButtonProps(btnProps);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setClickedButtonProps(null);
  };

  const processButtons = (buttons?: ButtonProps[]) => {
    return (buttons || []).map((btn) =>
      btn.triggerConfirmationModal
        ? {
            ...btn,
            onClick: () => handleOpenModal(btn)
          }
        : btn
    );
  };

  const handleConfirm = async () => {
    const clickedBtnSubmissionConfig = clickedButtonProps?.submissionConfig;
    if (props.submissionUrl) {
      const toastId = toast.info(clickedBtnSubmissionConfig?.loadingText || "Accepting Offer...", {
        autoClose: false,
        isLoading: true
      });

      const webhookPayload = {
        dealId: props.dealId,
        buttonText: clickedButtonProps?.text
      };
      setProcessingConfirmAction(true);
      try {
        await fetch(props.submissionUrl, {
          method: "POST",
          mode: "no-cors",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(webhookPayload)
        });
        toast.update(toastId, clickedBtnSubmissionConfig?.successText || "Offer Accepted!", {
          autoClose: 2000,
          type: TOAST_TYPE.SUCCESS,
          isLoading: false
        });
        setProcessingConfirmAction(false);
        handleModalClose();

        if (props.redirectSlug) {
          toast.success("Redirecting...", {
            autoClose: 1000
          });
          const redirectUrl = props.redirectSlug.startsWith("/") ? props.redirectSlug : `/${props.redirectSlug}`;
          router.push(redirectUrl);
        }
      } catch (err) {
        setProcessingConfirmAction(false);
        toast.update(toastId, clickedBtnSubmissionConfig?.errorText || "Failed to Accept Offer", {
          autoClose: 2000,
          type: TOAST_TYPE.ERROR,
          isLoading: false
        });
        logError({
          error: err as Error,
          source: "Tile - PropertyPage - handleConfirm",
          message: `Error request to webhook for ${clickedButtonProps?.text} Button click`,
          type: ERROR_TYPES.CLIENT_TILE,
          url: window.location.href,
          additionalInfo: {
            webhookUrl: props.submissionUrl,
            payload: webhookPayload
          }
        });
      }
    } else {
      handleModalClose();
    }
  };

  // Create an array of section components with their sort orders
  const sections = [
    {
      order: props.sectionSort?.hero ?? 1,
      component: (
        <HeroSection
          data={{ ...props.hero, buttons: processButtons(props.hero?.buttons) } as PropertyPageProps["hero"]}
        />
      )
    },
    { order: props.sectionSort?.navTabs ?? 2, component: <NavTabsSection data={props.navTabs} /> },
    {
      order: props.sectionSort?.investorMatch ?? 3,
      component: (
        <InvestorMatchSection
          data={
            {
              ...props.investorMatch,
              matchScore: props.investorMatch?.matchScore
                ? {
                    ...props.investorMatch?.matchScore,
                    cta: props.investorMatch.matchScore.cta
                      ? processButtons([props.investorMatch.matchScore.cta])[0]
                      : undefined
                  }
                : undefined
            } as PropertyPageProps["investorMatch"]
          }
        />
      )
    },
    {
      order: props.sectionSort?.dealHighlight ?? 4,
      component: (
        <DealHighlightSection
          data={
            {
              ...props.dealHighlight,
              cta: props.dealHighlight?.cta ? processButtons([props.dealHighlight.cta])[0] : undefined
            } as PropertyPageProps["dealHighlight"]
          }
        />
      )
    },
    {
      order: props.sectionSort?.investmentCalculator ?? 5,
      component: <InvestmentCalculatorSection data={props.investmentCalculator} />
    },
    {
      order: props.sectionSort?.propertyDetails ?? 6,
      component: (
        <PropertyDetailsSection
          data={
            {
              ...props.propertyDetails,
              buttons: processButtons(props.propertyDetails?.buttons)
            } as PropertyPageProps["propertyDetails"]
          }
          tabs={props.propertyTabs}
        />
      )
    },
    { order: props.sectionSort?.compsAnalysis ?? 7, component: <CompsAnalysisSection data={props.compsAnalysis} /> },
    {
      order: props.sectionSort?.acquisitionCriteria ?? 8,
      component: <AcquisitionCriteriaSection data={props.acquisitionCriteria} />
    },
    { order: props.sectionSort?.process ?? 9, component: <ProcessSection data={props.process} /> },
    {
      order: props.sectionSort?.finalCta ?? 10,
      component: (
        <FinalCtaSection
          data={
            { ...props.finalCta, buttons: processButtons(props.finalCta?.buttons) } as PropertyPageProps["finalCta"]
          }
        />
      )
    }
  ];

  // Sort sections by order and filter out null components
  const sortedSections = sections.sort((a, b) => a.order - b.order).filter((section) => section.component !== null);

  // Initialize stickyCta status
  const [showStickyCta, setShowStickyCta] = useState(false);

  // Add scroll event listener to control the sticky CTA
  useEffect(() => {
    if (!props.stickyCta || !props.stickyCta.enabled) return;

    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setShowStickyCta(scrollPosition > 500);
    };

    // Add event listener
    window.addEventListener("scroll", handleScroll);

    // Clean up
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [props.stickyCta]);

  const confirmModalConfig = clickedButtonProps?.modalConfig
    ? { ...DEFAULT_CONFIRMAITON_MODAL_CONFIG, ...clickedButtonProps.modalConfig }
    : DEFAULT_CONFIRMAITON_MODAL_CONFIG;

  return (
    <div className="bg-gray-50 font-sans leading-normal text-gray-800">
      {sortedSections.map((section, index) => (
        <React.Fragment key={index}>{section.component}</React.Fragment>
      ))}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        isConfirmLoading={processingConfirmAction}
        onConfirm={handleConfirm}
        title={confirmModalConfig.title}
        subtitle={confirmModalConfig.subtitle}
        confirmText={confirmModalConfig.confirmText}
        cancelText={confirmModalConfig.cancelText}
      />

      {props.stickyCta && props.stickyCta.enabled && showStickyCta && (
        <StickyCtaSection
          data={
            {
              ...props.stickyCta,
              button: props.stickyCta?.button ? processButtons([props.stickyCta?.button])[0] : undefined
            } as PropertyPageProps["stickyCta"]
          }
        />
      )}
    </div>
  );
};

export default PropertyPage;
